import React, { useEffect, useRef, useState } from 'react';

import { ErrorBox } from './Loader.jsx';
import { notify } from '../lib/notifications';


/**
 * Admin "set this user's password" dialog.
 *
 * Used on Business → Users → Edit (and any other ResourceForm that
 * exposes ``resource.setPassword``).  The caller is an org admin
 * resetting someone else's password, so we do NOT ask for the
 * target's current password — that is the ``/me#password`` self-service
 * flow.  Backend: ``POST /api/super_admin/users/:id/set-password/``.
 */
export default function SetPasswordModal({
  userId,
  userLabel,
  setPassword,
  onClose,
}) {
  const [password, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const firstField = useRef(null);

  useEffect(() => {
    firstField.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!password || password.length < 4) {
      setError('New password must be at least 4 characters.');
      return;
    }
    if (password !== confirm) {
      setError('New password and confirmation do not match.');
      return;
    }
    setSaving(true);
    try {
      await setPassword(userId, password);
      notify.success(
        userLabel
          ? `Password updated for ${userLabel}.`
          : 'Password updated successfully.',
        { title: 'Saved' },
      );
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="ty-modal-backdrop"
      onClick={() => { if (!saving) onClose(); }}
    >
      <form
        className="ty-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ty-set-pwd-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
      >
        <div className="ty-modal__head">
          <h3 id="ty-set-pwd-title" className="ty-modal__title">
            Change password
          </h3>
          <button
            type="button"
            className="ty-modal__close"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="ty-modal__body">
          {userLabel && (
            <p className="ty-muted" style={{ marginTop: 0 }}>
              Set a new password for <strong>{userLabel}</strong>.
              They will use it the next time they sign in.
            </p>
          )}
          <ErrorBox error={error} />
          <div className="ty-field">
            <label htmlFor="ty-set-pwd-new">New password *</label>
            <input
              ref={firstField}
              id="ty-set-pwd-new"
              type="password"
              className="ty-input"
              value={password}
              onChange={(e) => setPwd(e.target.value)}
              autoComplete="new-password"
              required
              minLength={4}
            />
          </div>
          <div className="ty-field">
            <label htmlFor="ty-set-pwd-confirm">Confirm new password *</label>
            <input
              id="ty-set-pwd-confirm"
              type="password"
              className="ty-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              minLength={4}
            />
          </div>
        </div>
        <div className="ty-modal__foot">
          <button
            type="button"
            className="ty-btn ty-btn--ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button type="submit" className="ty-btn" disabled={saving}>
            {saving ? 'Updating…' : 'Change password'}
          </button>
        </div>
      </form>
    </div>
  );
}
